import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import NumberInput from "./NumberInput";

// Champs : 44 px de haut au doigt, 36 px à la souris ; 16 px de texte au doigt (sinon iOS zoome la page).
const baseInput =
  "w-full min-h-[2.75rem] sm:min-h-[2.25rem] rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 shadow-[inset_0_1px_2px_rgb(16_32_26/0.04)] placeholder:text-foreground-400 transition-[border-color,box-shadow] duration-150 ease-out hover:border-background-400 focus:border-primary-500 focus:outline-none focus:ring-[3px] focus:ring-primary-100 disabled:cursor-not-allowed disabled:bg-background-100 disabled:text-foreground-500";

interface FieldWrapperProps {
  label?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function FieldWrapper({ label, hint, required, children, className = "" }: FieldWrapperProps) {
  return (
    <label className={`block ${className}`}>
      {label && (
        <span className="mb-1.5 block text-sm font-medium text-foreground-700">
          {label}
          {required && <span className="text-accent-600"> *</span>}
        </span>
      )}
      {children}
      {hint && <span className="mt-1 block text-xs text-foreground-500">{hint}</span>}
    </label>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  wrapperClass?: string;
}

/** Champ de saisie ; avec `type="number"`, les milliers sont séparés pendant la saisie (« 20 000 »). */
export function Input({ label, hint, wrapperClass, className = "", ...rest }: InputProps) {
  return (
    <FieldWrapper label={label} hint={hint} required={rest.required} className={wrapperClass}>
      {rest.type === "number" ? (
        <NumberInput {...rest} className={`${baseInput} ${className}`} />
      ) : (
        <input {...rest} className={`${baseInput} ${className}`} />
      )}
    </FieldWrapper>
  );
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  wrapperClass?: string;
}

export function Select({ label, hint, wrapperClass, className = "", children, ...rest }: SelectProps) {
  return (
    <FieldWrapper label={label} hint={hint} required={rest.required} className={wrapperClass}>
      <select {...rest} className={`${baseInput} cursor-pointer ${className}`}>
        {children}
      </select>
    </FieldWrapper>
  );
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  wrapperClass?: string;
}

export function Textarea({ label, hint, wrapperClass, className = "", ...rest }: TextareaProps) {
  return (
    <FieldWrapper label={label} hint={hint} required={rest.required} className={wrapperClass}>
      <textarea {...rest} className={`${baseInput} resize-none ${className}`} />
    </FieldWrapper>
  );
}